/**
 * Order List Share Functionality
 * Features: Export PDF, Share Link, Send via WhatsApp
 */

(function () {
  'use strict';

  /* ── Colors ──────────────────────────────────────────────────────────── */
  const C = {
    dark: '#1a4230',
    darker: '#0d2818',
    light: '#e8f0eb',
    gold: '#ceaa57',
  };

  /* ── Toast ───────────────────────────────────────────────────────────── */
  function showToast(msg, duration = 2800) {
    let t = document.querySelector('.m-share-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'm-share-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('m-share-toast--visible');
    clearTimeout(t._hideTimer);
    t._hideTimer = setTimeout(() => t.classList.remove('m-share-toast--visible'), duration);
  }

  /* ── Read SKU robustly ───────────────────────────────────────────────── */
  function readSku(el) {
    // 1. data-sku attribute (set in cart-drawer-item.liquid)
    let sku = (el.getAttribute('data-sku') || '').trim();
    if (sku) return sku;

    // 2. Any child element that might show SKU text
    const skuEl = el.querySelector(
      '[data-item-sku], .cart-item__sku, .m-cart-item__sku, .product-sku'
    );
    if (skuEl) {
      sku = skuEl.textContent.replace(/SKU[:\s]*/i, '').trim();
      if (sku) return sku;
    }

    // 3. Fallback: use product-id suffix
    const pid = (el.getAttribute('data-product-id') || '').trim();
    if (pid) return 'P-' + pid.slice(-4);

    return '–';
  }

  /* ── Collect cart items ──────────────────────────────────────────────── */
  function getCartItems() {
    const items = [];
    document.querySelectorAll('[data-cart-item]').forEach((el) => {
      const titleEl = el.querySelector('.m-cart-item__title');
      const priceEl = el.querySelector('.m-cart-item__price--regular, .m-cart-item__price--final');
      const imgEl = el.querySelector('.m-cart-item__image img, .m-cart-item__image source');
      const qtyEl = el.querySelector('.m-quantity__input');
      const variantEls = el.querySelectorAll('.m-cart-item__option');

      const title = titleEl ? titleEl.textContent.trim() : '';
      if (!title) return;

      const price = priceEl ? priceEl.textContent.trim() : '';
      const qty = qtyEl ? qtyEl.value : '1';
      const sku = readSku(el);

      const variants = [];
      variantEls.forEach(v => { const t = v.textContent.trim(); if (t) variants.push(t); });

      // packaging = first variant (e.g. "שק 5 קג")
      const packaging = variants.length > 0 ? variants[0] : '–';

      let imgSrc = '';
      if (imgEl) {
        imgSrc = imgEl.src || imgEl.srcset || '';
        if (imgEl.srcset) {
          imgSrc = imgEl.srcset.split(',')[0].trim().split(' ')[0];
        }
      }

      items.push({ title, price, qty, imgSrc, variants, sku, packaging });
    });
    return items;
  }

  /* ── Subtotal ────────────────────────────────────────────────────────── */
  function getSubtotal() {
    const el = document.querySelector('[data-cart-subtotal-price], .m-cart__subtotal--price');
    return el ? el.textContent.trim() : '';
  }

  /* ── Logo ────────────────────────────────────────────────────────────── */
  function getLogoHTML() {
    const url = 'https://cdn.shopify.com/s/files/1/0706/5065/8986/files/Untitled_design_-_2026-05-06T093330.631_c7e68585-a60d-4c1c-a84d-1090cb69ef35.png?v=1778390783';
    return `<img class="print-shop-logo" src="${url}" alt="Logo" />`;
  }

  /* ── Customer details ────────────────────────────────────────────────── */
  function getCustomerDetails() {
    return {
      name:  (document.getElementById('m-customer-name')?.value || '').trim(),
      phone: (document.getElementById('m-customer-phone')?.value || '').trim(),
      city:  (document.getElementById('m-customer-city')?.value || '').trim(),
    };
  }

  function validateCustomerDetails() {
    const d = getCustomerDetails();
    if (!d.name || !d.phone || !d.city) {
      showToast('⚠️ נא למלא שם לקוח, מספר טלפון ועיר.');
      return false;
    }
    return true;
  }

  /* ══════════════════════════════════════════════════════════════════════
     BUILD PDF
  ══════════════════════════════════════════════════════════════════════ */
  function exportPDF() {
    if (!validateCustomerDetails()) return;
    const items = getCartItems();
    const details = getCustomerDetails();

    if (!items.length) { showToast('⚠️ ההזמנה ריקה.'); return; }

    const old = document.querySelector('.m-order-list-print-area');
    if (old) old.remove();

    const area = document.createElement('div');
    area.className = 'm-order-list-print-area';
    area.setAttribute('dir', 'rtl');
    area.setAttribute('lang', 'he');

    const now = new Date();
    const date = now.toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
    const ordNo = 'ORD-' + now.getFullYear() + '-' + String(Math.floor(10000 + Math.random() * 90000)).padStart(5, '0');

    /* ── Rows ── */
    let rowsHTML = '';
    items.forEach(item => {
      const metaText = item.variants.length > 1 ? item.variants.slice(1).join(' · ') : '';
      const metaHTML = metaText ? `<div class="print-item-meta">${metaText}</div>` : '';
      const imgHTML = item.imgSrc
        ? `<img class="print-item-img" src="${item.imgSrc}" alt="" />`
        : `<div class="print-item-img print-item-img--empty"></div>`;

      rowsHTML += `
        <tr>
        <td class="td-product">
          <div class="product-cell">
            ${imgHTML}
            <div class="product-text">
              <div class="product-title">${item.title}</div>
              ${metaHTML}
            </div>
          </div>
        </td>
        <td class="td-qty"><span class="qty-pill">${item.qty}</span></td>
        <td class="td-sku">${item.sku}</td>
        <td class="td-pack">${item.packaging}</td>
          <td class="td-notes"><span class="dash">–</span></td>
        </tr>`;
    });

    area.innerHTML = `
<!-- ▌HEADER ▌-->
<div class="ph">
  <div class="ph-top">
  <div class="ph-brand">
    ${getLogoHTML()}
    <span class="ph-sub">ספק סיטונאי מסעדות · SINCE 1956</span>
  </div>
    <div class="ph-badge">קטלוג הזמנה</div>
  </div>
  <div class="ph-meta">
    <div class="ph-meta-item">
      <span class="ml">תאריך</span>
      <span class="mv">${date}</span>
    </div>
    <div class="ph-meta-item">
      <span class="ml">מספר הזמנה</span>
      <span class="mv">${ordNo}</span>
    </div>
  </div>
</div>

<!-- ▌CUSTOMER STRIP ▌-->
<div class="cs">
  <div class="cs-item">
    <span class="cs-label">עיר</span>
    <span class="cs-val">${details.city}</span>
  </div>
  <div class="cs-item">
    <span class="cs-label">מספר טלפון</span>
    <span class="cs-val">${details.phone}</span>
  </div>
  <div class="cs-item cs-item--last">
    <span class="cs-label">לקוח</span>
    <span class="cs-val">${details.name}</span>
  </div>
</div>

<!-- ▌TABLE ▌-->
<div class="pt-wrap">
  <table class="pt">
    <thead>
      <tr>
      <th class="td-product">מוצר</th>  
      <th class="td-qty">כמות</th>  
        <th class="td-sku">מק&quot;ט</th>
        <th class="td-pack">יחידת אריזה</th>
        <th class="td-notes">הערת לקוח</th>
      </tr>
    </thead>
    <tbody>${rowsHTML}</tbody>
  </table>
</div>

<!-- ▌SPACER ▌-->
<div class="spacer"></div>

<!-- ▌SIGNATURE SECTION ▌-->
<div class="sign-section">
  <div class="sign-gold-line"></div>
  <div class="sign-row">
    <div class="sign-col">
      <div class="sign-label">תַאֲרִיך</div>
      <div class="sign-line"></div>
    </div>
    <div class="sign-col">
      <div class="sign-label">חתימת לקוח</div>
      <div class="sign-line"></div>
    </div>
  </div>
</div>

<!-- ▌FOOTER ▌-->
<div class="pf">
  <div class="pf-left">
    <span class="pf-note">מסמך זה נוצר באופן אוטומטי ואינו חשבונית מס.</span>
  </div>
  <div class="pf-right">
    <div class="pf-brand">בני חנא שמשום בע״מ</div>
    <div class="pf-sub">ספק סיטונאי מסעדות · Since 1956</div>
  </div>
</div>`;

    document.body.appendChild(area);
    setTimeout(() => { window.print(); setTimeout(() => area.remove(), 1200); }, 150);
  }

  /* ── Share link ──────────────────────────────────────────────────────── */
  function shareLink() {
    if (!validateCustomerDetails()) return;
    const items = getCartItems();
    const details = getCustomerDetails();
    if (!items.length) { showToast('⚠️ ההזמנה ריקה.'); return; }

    const url = window.location.origin + '/cart';
    const text = buildText(items, getSubtotal(), details);

    if (navigator.share) {
      navigator.share({ title: 'רשימת ההזמנה', text, url }).catch(() => copy(url));
    } else {
      copy(url);
    }
  }

  function copy(text) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => showToast('✅ הקישור הועתק!'))
        .catch(() => legacyCopy(text));
    } else { legacyCopy(text); }
  }

  function legacyCopy(text) {
    const ta = Object.assign(document.createElement('textarea'), {
      value: text, style: 'position:fixed;opacity:0'
    });
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); showToast('✅ הקישור הועתק!'); }
    catch { showToast('❌ לא ניתן להעתיק.'); }
    document.body.removeChild(ta);
  }

  /* ── WhatsApp text ───────────────────────────────────────────────────── */
  function buildText(items, subtotal, d) {
    let t = '🛒 *רשימת הזמנה*\n\n';
    t += `*לקוח:* ${d.name}\n*מספר טלפון:* ${d.phone}\n*עיר:* ${d.city}\n\n────────────────\n\n`;
    items.forEach((item, i) => {
      t += `${i + 1}. *${item.title}*\n`;
      t += `   מק"ט: ${item.sku}  |  אריזה: ${item.packaging}\n`;
      t += `   כמות: ${item.qty}\n\n`;
    });
    if (subtotal) t += `*סה״כ: ${subtotal}*\n`;
    t += `\n${window.location.origin}`;
    return t;
  }

  /* ── WhatsApp ────────────────────────────────────────────────────────── */
  function sendWhatsApp() {
    if (!validateCustomerDetails()) return;
    const items = getCartItems();
    if (!items.length) { showToast('⚠️ ההזמנה ריקה.'); return; }
    const text = buildText(items, getSubtotal(), getCustomerDetails());
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  }

  /* ── WhatsApp Direct (sends to business number) ─────────────────────── */
  const BUSINESS_WHATSAPP = '972523109222';
  function sendWhatsAppDirect() {
    if (!validateCustomerDetails()) return;
    const items = getCartItems();
    if (!items.length) { showToast('⚠️ ההזמנה ריקה.'); return; }
    const text = buildText(items, getSubtotal(), getCustomerDetails());
    window.open(`https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  }

  /* ── Listeners ───────────────────────────────────────────────────────── */
  function attachListeners() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('#m-btn-export-pdf');
      if (!btn) return;
      btn.classList.add('m-btn-loading');
      setTimeout(() => { exportPDF(); btn.classList.remove('m-btn-loading'); }, 200);
    });
    document.addEventListener('click', e => { if (e.target.closest('#m-btn-share-link')) shareLink(); });
    document.addEventListener('click', e => { if (e.target.closest('#m-btn-send-whatsapp')) sendWhatsApp(); });
    document.addEventListener('click', e => { if (e.target.closest('#m-btn-send-whatsapp-direct')) sendWhatsAppDirect(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachListeners);
  } else {
    attachListeners();
  }
})();
